import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ArrowsAiCustomToolsAPI from 'dashboard/api/arrowsAi/customTools';
import {
  parseAPIErrorResponse,
  throwErrorMessage,
} from 'dashboard/store/utils/api';

const normalizeParam = raw => ({
  name: raw.name || '',
  type: raw.type || 'string',
  description: raw.description || '',
  required: Boolean(raw.required),
});

const normalizeTool = raw => ({
  id: Number(raw.id),
  name: raw.name || '',
  description: raw.description || '',
  slug: raw.slug || '',
  endpoint: raw.endpoint_url || raw.endpoint || '',
  httpMethod: raw.http_method || raw.httpMethod || 'GET',
  authType: raw.auth_type || raw.authType || 'none',
  authHeader: raw.auth_header || raw.authHeader || '',
  authConfigured: Boolean(raw.auth_configured ?? raw.authConfigured),
  paramSchema: Array.isArray(raw.param_schema || raw.paramSchema)
    ? (raw.param_schema || raw.paramSchema).map(normalizeParam)
    : [],
  enabled: raw.enabled !== false,
  createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
});

export const toToolPayload = (payload, { includeToken } = {}) => {
  const body = {
    name: payload.name.trim(),
    description: payload.description.trim(),
    endpoint_url: payload.endpoint.trim(),
    http_method: payload.httpMethod,
    auth_type: payload.authType,
    auth_header: payload.authHeader.trim(),
    enabled: payload.enabled,
    param_schema: payload.paramSchema
      .filter(item => item.name.trim())
      .map(item => ({
        name: item.name.trim().toLowerCase(),
        type: item.type || 'string',
        description: item.description.trim(),
        required: Boolean(item.required),
      })),
  };

  if (includeToken && payload.authToken?.trim()) {
    body.auth_token = payload.authToken.trim();
  }

  return body;
};

export function useArrowsAiTools(assistantId) {
  const { t } = useI18n();
  const tools = ref([]);
  const isLoading = ref(false);
  let loadedAssistantId = null;

  const fetchTools = async (id, { force } = {}) => {
    if (!id) {
      tools.value = [];
      loadedAssistantId = null;
      return;
    }
    if (!force && loadedAssistantId === id) return;

    isLoading.value = true;
    try {
      const { data } = await ArrowsAiCustomToolsAPI.get({ assistantId: id });
      tools.value = (Array.isArray(data) ? data : []).map(normalizeTool);
      loadedAssistantId = id;
    } catch (error) {
      tools.value = [];
      loadedAssistantId = id;
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string' ? message : t('ARROWS_AI.TOOLS.FETCH_ERROR')
      );
    } finally {
      isLoading.value = false;
    }
  };

  watch(assistantId, id => fetchTools(id), { immediate: true });

  const createTool = async payload => {
    try {
      const { data } = await ArrowsAiCustomToolsAPI.create({
        assistantId: assistantId.value,
        payload: toToolPayload(payload, { includeToken: true }),
      });
      const tool = normalizeTool(data);
      tools.value = [tool, ...tools.value];
      return tool;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const updateTool = async (id, payload) => {
    try {
      const { data } = await ArrowsAiCustomToolsAPI.update({
        assistantId: assistantId.value,
        id,
        payload: toToolPayload(payload, { includeToken: true }),
      });
      const updated = normalizeTool(data);
      tools.value = tools.value.map(item =>
        item.id === updated.id ? updated : item
      );
      return updated;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteTools = async ids => {
    const uniqueIds = [...new Set(ids.map(Number))];
    try {
      await Promise.all(
        uniqueIds.map(id =>
          ArrowsAiCustomToolsAPI.delete({ assistantId: assistantId.value, id })
        )
      );
      const idSet = new Set(uniqueIds);
      tools.value = tools.value.filter(item => !idSet.has(item.id));
    } catch (error) {
      await fetchTools(assistantId.value, { force: true });
      throwErrorMessage(error);
    }
  };

  return {
    tools: computed(() => tools.value),
    isLoading: computed(() => isLoading.value),
    createTool,
    updateTool,
    deleteTools,
  };
}
